<template>
  <span class="ru-plate" :class="`ru-plate--${size}`">
    <span class="ru-plate__main">
      <template v-if="parts.isStandard">
        <span class="ru-plate__letter">{{ parts.letterLeft }}</span>
        <span class="ru-plate__digits">{{ parts.digits }}</span>
        <span class="ru-plate__letters-right">
          {{ parts.letterRight }}
        </span>
      </template>
      <span v-else class="ru-plate__raw">{{ parts.raw }}</span>
    </span>
    <span
      class="ru-plate__region"
      v-if="parts.isStandard && parts.region"
    >
      <span class="ru-plate__region-code">{{ parts.region }}</span>
      <span class="ru-plate__flag">
        <svg
          viewBox="0 0 3 2"
          xmlns="http://www.w3.org/2000/svg"
          class="ru-plate__flag-svg"
        >
          <rect width="3" height="0.667" fill="#fff" />
          <rect y="0.667" width="3" height="0.667" fill="#0033A0" />
          <rect y="1.333" width="3" height="0.667" fill="#D52B1E" />
        </svg>
      </span>
    </span>
  </span>
</template>

<script>
const PLATE_PATTERNS = [
  // Стандартный легковой/такси: А123ВС77 или А123ВС777
  {
    type: "standard",
    regex: /^([А-ЯA-Z])(\d{3})([А-ЯA-Z]{2})(\d{2,3})?$/,
    map: (m) => ({
      letterLeft: m[1],
      digits: m[2],
      letterRight: m[3],
      region: m[4] || "",
    }),
  },
  // Прицепы/полуприцепы: АВ123477 или АВ1234777
  {
    type: "trailer",
    regex: /^([А-ЯA-Z]{2})(\d{4})(\d{2,3})?$/,
    map: (m) => ({
      letterLeft: m[1],
      digits: m[2],
      letterRight: "",
      region: m[3] || "",
    }),
  },
  // Мотоциклы/мопеды: 1234АВ56 или 1234АВ567
  {
    type: "moto",
    regex: /^(\d{4})([А-ЯA-Z]{2})(\d{2,3})?$/,
    map: (m) => ({
      letterLeft: "",
      digits: m[1],
      letterRight: m[2],
      region: m[3] || "",
    }),
  },
];

export default {
  name: "VehicleRegNumberBadge",
  props: {
    number: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "md", // sm | md | lg
    },
  },
  computed: {
    parts() {
      if (!this.number) {
        return { isStandard: false, raw: "" };
      }

      // Убираем все пробелы
      const normalized = this.number
        .replace(/[\s\u00A0]+/g, "")
        .toUpperCase();

      for (const pattern of PLATE_PATTERNS) {
        const match = normalized.match(pattern.regex);
        if (match) {
          const parsed = pattern.map(match);
          return {
            isStandard: true,
            plateType: pattern.type,
            ...parsed,
          };
        }
      }

      return { isStandard: false, raw: normalized };
    },
  },
};
</script>

<style scoped>
.ru-plate {
  display: inline-flex;
  align-items: stretch;
  box-sizing: border-box;
  border: 2px solid #1a1a1a;
  border-radius: 4px;
  background: #fff;
  font-family: "FE-Schrift", "Roboto Condensed", "Arial Narrow", Arial,
    sans-serif;
  overflow: hidden;
  vertical-align: middle;
  flex-shrink: 0;
  width: fit-content;
  min-width: var(--ru-plate-min-width);
  line-height: 1;
}

.ru-plate__main {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  color: #1a1a1a;
  background: #fff;
}

.ru-plate__digits,
.ru-plate__raw {
  display: flex;
  align-items: center;
}

.ru-plate__letter,
.ru-plate__letters-right {
  display: flex;
  align-items: flex-end;
  font-size: 0.9em;
  transform: translateY(0.09em);
}

.ru-plate__region {
  display: flex;
  flex-direction: row;
  flex: 0 0 var(--ru-plate-region-width);
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  background: #fff;
  border-left: 2px solid #1a1a1a;
}

.ru-plate__region-code {
  font-weight: 800;
  color: #1a1a1a;
  line-height: 1;
  letter-spacing: 1px;
  white-space: nowrap;
}

.ru-plate__flag {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ru-plate__flag-svg {
  display: block;
  border: 0.5px solid #bbb;
  border-radius: 1px;
}

.ru-plate--sm {
  --ru-plate-min-width: 108px;
  --ru-plate-region-width: 38px;

  height: 24px;
  border-width: 1.5px;
}

.ru-plate--sm .ru-plate__main {
  padding: 0 5px;
  gap: 1px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
}

.ru-plate--sm .ru-plate__region {
  padding: 0 4px;
  gap: 3px;
  border-left-width: 1.5px;
}

.ru-plate--sm .ru-plate__region-code {
  font-size: 13px;
  font-weight: 800;
}

.ru-plate--sm .ru-plate__flag-svg {
  width: 18px;
  height: 12px;
}

.ru-plate--md {
  --ru-plate-min-width: 144px;
  --ru-plate-region-width: 52px;

  height: 32px;
  border-width: 2px;
}

.ru-plate--md .ru-plate__main {
  padding: 0 8px;
  gap: 1px;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 1.5px;
}

.ru-plate--md .ru-plate__region {
  padding: 0 6px;
  gap: 5px;
  border-left-width: 2px;
}

.ru-plate--md .ru-plate__region-code {
  font-size: 17px;
  font-weight: 800;
}

.ru-plate--md .ru-plate__flag-svg {
  width: 24px;
  height: 16px;
}

.ru-plate--lg {
  --ru-plate-min-width: 190px;
  --ru-plate-region-width: 68px;

  height: 42px;
  border-width: 2.5px;
}

.ru-plate--lg .ru-plate__main {
  padding: 0 12px;
  gap: 2px;
  font-size: 24px;
  font-weight: 900;
  letter-spacing: 2px;
}

.ru-plate--lg .ru-plate__region {
  padding: 0 8px;
  gap: 6px;
  border-left-width: 2.5px;
}

.ru-plate--lg .ru-plate__region-code {
  font-size: 24px;
  font-weight: 900;
}

.ru-plate--lg .ru-plate__flag-svg {
  width: 32px;
  height: 21px;
}
</style>
